export const NUM_SENDER_PROFILES_LIMIT = 5;
export const VERIFICATION_POLLING_TIMEOUT_MS = 80000;

export interface SenderProfilePhysicalAddress {
  id?: number;
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  region: string;
  postalCode: string;
  countryCode: string;
  phone: string;
  fax: string;
}

export interface SenderProfile {
  id: number;
  name: string;
  email: string;
  isVerified: boolean;
  isSuspended: boolean;
  isDefault: boolean;
  physicalAddress: SenderProfilePhysicalAddress;
  isCustomDomainEnabled: boolean;
  isCustomDomainVerified: boolean;
  facebook?: string;
  twitter?: string;
  pinterest?: string;
  instagram?: string;
}

export interface CreateSenderProfileResponse {
  senderProfile: SenderProfile;
  sniperLink: string | null;
}

export enum DomainAuthenticationStatus {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  TEMPORARY_FAILURE = 'TEMPORARY_FAILURE',
  NOT_STARTED = 'NOT_STARTED',
}

export enum DnsRecordType {
  CNAME = 'CNAME',
  TXT = 'TXT',
}

export interface DomainAuthenticationDnsRecord {
  type: DnsRecordType;
  hostName: string;
  value: string;
  isOptional: boolean;
}

export interface DomainAuthenticationDetails {
  status: DomainAuthenticationStatus;
  dnsRecords: DomainAuthenticationDnsRecord[];
  authenticatedOn: string | null;
}

export const UNAUTHENTICATED_DOMAIN_DETAILS: DomainAuthenticationDetails = {
  status: DomainAuthenticationStatus.NOT_STARTED,
  dnsRecords: [],
  authenticatedOn: null,
};

export interface DomainAuthenticationDetailsResponse {
  domainAuthenticationDetails: Record<string, DomainAuthenticationDetails>;
}

export interface DomainPayload {
  domain: string;
}

export interface GenerateDnsRecordsResponse {
  dnsRecords: DomainAuthenticationDnsRecord[];
}

export interface GeneratedDnsRecords {
  domain: string;
  dnsRecords: DomainAuthenticationDnsRecord[];
}

export interface EmailMarketingCampaignSettingsState {
  settingsLoadingState: number;
  senderProfiles: Record<number, SenderProfile>;
  emailSends: number;
  credits: number | null;
  defaultSenderProfileId: number | null;
  isCustomDomainEnabledForPremiumPlus?: boolean;
  accountEmail: string;
  domainAuthenticationDetails: Record<string, DomainAuthenticationDetails>;
}

export interface SenderProfileIdPayload {
  idSenderProfile: number;
}

export interface SenderProfilePayload {
  name: string;
  email: string;
  isCustomDomainEnabled: boolean;
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  region: string;
  postalCode: string;
  countryCode: string;
  phone: string;
  fax: string;
  facebook: string;
  twitter: string;
  instagram: string;
  pinterest: string;
}

export interface UpdateSenderProfilePayload extends SenderProfilePayload {
  idSenderProfile: number;
  idPhysicalAddress?: number;
}

export interface InitEmailMarketingCampaignSettingsResponse {
  senderProfiles: Record<number, SenderProfile>;
  domainAuthenticationDetails: Record<string, DomainAuthenticationDetails>;
  emailSends: number;
  credits: number | null;
  defaultSenderProfileId: number | null;
  isCustomDomainEnabledForPremiumPlus: boolean;
  accountEmail: string;
}

export interface DeleteSenderProfileResponse {
  deletedSenderProfileId: number;
  newDefaultSenderProfileId?: number;
}

export interface UpdateDefaultSenderProfileResponse {
  previousDefaultSenderProfileId: number;
  newDefaultSenderProfileId: number;
}

export interface IsEmailVerifiedResponse {
  isVerified: boolean;
  idSenderProfile: number;
}

export interface IsEmailVerifiedBackendResponse {
  isVerified: boolean;
  idSenderProfile: string;
}

export interface CheckEmailVerificationResponse {
  isAvailable: boolean;
  data: {
    idSenderProfile: number;
  };
}

export interface CheckEmailVerificationBackendResponse {
  isAvailable: boolean;
  data: {
    idSenderProfile: string;
  };
}
